import { Component, computed, effect, OnInit, signal } from '@angular/core';
import { FleetManagementService } from '../fleet-management.service';
import { FleetClient } from '../../models/client';
import { AppModule } from "../../app.module";
import { FleetManagementEdit } from '../fleet-management-edit/fleet-management-edit';
import { ClientService } from '../../shared/client.service';
import { NotificationService } from '../../shared/notification.service';
import { AuthService } from '../../auth.service';
import { Globals } from '../../shared/functions';

@Component({
  selector: 'app-fleet-management',
  imports: [AppModule, FleetManagementEdit],
  templateUrl: './fleet-management.html',
  styleUrl: './fleet-management.less'
})
export class FleetManagement implements OnInit {

  fleetClients = signal<FleetClient[]>([]);
  searchTerm = signal<string>('');
  isLoading = signal<boolean>(false);

  isModalVisible = signal(false);
  selectedFleet = signal<FleetClient | null>(null);
  selectedForMerge = signal<FleetClient[]>([]);


  filteredFleets = computed(() => {
    const term = this.searchTerm().toLowerCase();
    const list = this.fleetClients();
    let fleetsFiltered = list;

    if (term && term.trim() !== '' && term.length >= 2)
      return fleetsFiltered.filter(fleet => 
        fleet.clientName.toLowerCase().includes(term) ||
        fleet.clientTel.toLowerCase().includes(term)
    );
    return fleetsFiltered;

  })

  constructor(
    private clientService: ClientService,
    private notification: NotificationService,
    private authService: AuthService
  ) { }

  ngOnInit() {
    this.loadFleetClients();
  }

  private loadFleetClients() {
    this.clientService.getFleetClients().subscribe({
      next: (fleets: FleetClient[]) => {
        this.fleetClients.set(fleets);
      },
      error: (error) => {
        console.error('Error fetching fleet clients:', error);
        this.notification.showError('Error fetching fleet clients');
      }
    });
  }

  public openNewFleetModal() {
    this.selectedFleet.set(null);
    this.isModalVisible.set(true);
  }

  public viewDetails(fleet: FleetClient) {
    this.selectedForMerge.set([]);
    this.selectedFleet.set(fleet);
    this.isModalVisible.set(true);
  }

  public removeFleetConnection(clientId: number) {
    if (confirm('Are you sure you want to remove this fleet connection?')) {
      this.clientService.removeFleetClient(clientId).subscribe({
        next: () => {
          this.fleetClients.update(fleets => fleets.filter(f => f.clientId !== clientId));
          this.notification.showSuccess('Fleet connection removed successfully');
        },
        error: (error) => {
          console.error('Error removing fleet connection:', error);
          this.notification.showError('Error removing fleet connection');
        }
      });
    }
  }

  public getNewIncidentLink(fleetId: number): string {
    const callcentreId = this.authService.user?.clientId;
    if (!callcentreId) {
      return '';
    }

    const cvv = Globals.Functions.createFleetCvv(fleetId, callcentreId);
    return `${window.location.origin}/new-breakdown/${fleetId}/${cvv}/${callcentreId}`;
  }

  public copyNewIncidentLink(fleetId: number) {
    navigator.clipboard.writeText(this.getNewIncidentLink(fleetId));
    this.notification.showSuccess('New incident link copied');
  }

  public onFleetSaved(fleet: FleetClient) {
    const existingIndex = this.fleetClients().findIndex(c => c.clientId === fleet.clientId);
    if (existingIndex !== -1) {
      // Update existing fleet
      const updatedFleets = [...this.fleetClients()];
      updatedFleets[existingIndex] = fleet;
      this.fleetClients.update(() => updatedFleets);
      this.isModalVisible.set(false);
    } else {
      // Add new fleet
      this.fleetClients.update(fleets => [...fleets, fleet]);
      this.isModalVisible.set(false);
    }
  }

  public onFleetUpdated(fleet: FleetClient) {
    const existingIndex = this.fleetClients().findIndex(c => c.clientId === fleet.clientId);
    if (existingIndex !== -1) {
      // Update existing fleet
      const updatedFleets = [...this.fleetClients()];
      updatedFleets[existingIndex] = fleet;
      this.fleetClients.update(() => updatedFleets);
    }

  }

  public toggleFleetForMerge(fleet: FleetClient, checked: Event) {
    const isChecked = (checked.target as HTMLInputElement).checked;
    if (isChecked) {
      this.selectedForMerge.update(fleets => [...fleets, fleet]);
    } else {
      this.selectedForMerge.update(fleets => fleets.filter(f => f.clientId !== fleet.clientId));
    }
  }

  public isSelectedForMerge(clientId: number): boolean {
    if (clientId) {
      return this.selectedForMerge().some(fleet => fleet.clientId === clientId);
    }
    return false;
  }

  public openMergeFleetModal() {
    this.selectedFleet.set(this.selectedForMerge()[0])
    this.isModalVisible.set(true);
  }

public onFleetsMerged(sourceFleets: FleetClient[]) {
  const sourceIds = sourceFleets.map(f => f.clientId);
  this.fleetClients.update(fleets => 
    fleets.filter(f => !sourceIds.includes(f.clientId))
  );
  this.selectedForMerge.set([]);
}
}
